
<template>
  <div
    id="start-day-check-modal"
    ref="modalEl"
    class="modal modal-fixed-footer start-day-modal"
  >
    <div ref="contentEl" class="modal-content">
      <h5 class="modal-title">Opening Checks</h5>

      <p class="modal-subtitle">
        Confirm whether the venue is trading before completing
        the daily opening checklist.
      </p>

      <!-- VENUE CLOSED -->
      <div
        class="venue-closed-panel"
        :class="{ 'is-closed': venueClosed }"
      >
        <label class="venue-closed-option">
          <input
            v-model="venueClosed"
            type="checkbox"
            :disabled="submitting"
          />
          <span class="venue-closed-title">
            Venue Closed
          </span>
        </label>

        <p class="venue-closed-description">
          Select this if the venue will not be opening for
          trading on this date.
        </p>

        <p v-if="venueClosed" class="closed-confirmation">
          The venue will be recorded as closed. Opening checks
          will not be required, and the closing checklist will
          automatically be skipped at End Day or End Week.
        </p>
      </div>

      <!-- NORMAL OPENING QUESTIONS -->
      <template v-if="!venueClosed">
        <div class="section-heading">
          Existing Daily Checks
        </div>

        <div
          v-for="question in existingQuestions"
          :key="question.id"
          class="card-panel question-card"
        >
          <p class="question-title">
            {{ question.label }}
          </p>

          <div class="answer-row">
            <label
              v-for="option in ['yes', 'no']"
              :key="option"
            >
              <input
                v-model="form[question.id].answer"
                type="radio"
                :name="question.id"
                :value="option"
                :disabled="submitting"
              />
              <span>
                {{ option === "yes" ? "Yes" : "No" }}
              </span>
            </label>
          </div>

          <div
            v-if="form[question.id].answer === question.failure"
            class="comment-box"
          >
            <label class="active">
              Comments / corrective action
            </label>

            <textarea
              v-model.trim="form[question.id].comment"
              class="materialize-textarea browser-default"
              :disabled="submitting"
              placeholder="Record the problem and action taken"
            ></textarea>
          </div>
        </div>

        <div class="section-heading">
          Fire Safety — Opening Checks
        </div>

        <p class="grey-text text-darken-2">
          Check the premises against the fire safety procedures
          before allowing members of the public to enter.
        </p>

        <div
          v-for="question in fireQuestions"
          :key="question.id"
          class="card-panel question-card"
        >
          <p class="question-title">
            {{ question.label }}
          </p>

          <p
            v-if="question.help"
            class="question-help"
          >
            {{ question.help }}
          </p>

          <div class="answer-row">
            <label
              v-for="option in ['yes', 'no']"
              :key="option"
            >
              <input
                v-model="fire[question.id].answer"
                type="radio"
                :name="'fire_' + question.id"
                :value="option"
                :disabled="submitting"
              />
              <span>
                {{ option === "yes" ? "Yes" : "No" }}
              </span>
            </label>
          </div>

          <div
            v-if="fire[question.id].answer === 'no'"
            class="comment-box"
          >
            <label class="active">
              Defect / corrective action
            </label>

            <textarea
              v-model.trim="fire[question.id].comment"
              class="materialize-textarea browser-default"
              :disabled="submitting"
              placeholder="Describe the defect and action taken"
            ></textarea>
          </div>
        </div>

        <p class="safety-notice">
          Any defect affecting safe escape or fire protection
          must be escalated and remedied appropriately before
          opening. Recording a defect does not make the
          premises safe.
        </p>
      </template>

      <p v-if="error" class="red-text error-message">
        {{ error }}
      </p>
    </div>

    <div class="modal-footer custom-modal-footer">
      <button
        type="button"
        class="btn-flat"
        :disabled="submitting"
        @click="close"
      >
        Cancel
      </button>

      <button
        type="button"
        class="btn blue-grey darken-1"
        :disabled="submitting"
        @click="submitForm"
      >
        {{
          submitting
            ? "Processing..."
            : venueClosed
              ? "Confirm Venue Closed"
              : "Submit Checks"
        }}
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  reactive,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";

defineProps({
  submitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["submit"]);

const modalEl = ref(null);
const contentEl = ref(null);
const error = ref("");
const venueClosed = ref(false);

let modalInstance = null;

// ---------------------------------------------------------
// ORIGINAL DAILY CHECKS
// ---------------------------------------------------------

const existingQuestions = [
  {
    id: "fireExits",
    label:
      "Are all fire exits and internal/external escape routes clear and unobstructed?",
    failure: "no",
  },
  {
    id: "cctv",
    label: "Is CCTV working and recording?",
    failure: "no",
  },
  {
    id: "pest",
    label: "Has pest activity been observed recently?",
    failure: "yes",
  },
];

// ---------------------------------------------------------
// FIRE SAFETY QUESTIONS
//
// Duplicate escape_routes question removed.
// Its requirements are now included in fireExits above.
// ---------------------------------------------------------


const fireQuestions = [
  {
    id: "fire_doors",
    label:
      "Are fire doors unobstructed and operating correctly, with no unauthorised wedges or hold-open devices?",
    help:
      "Check that self-closing devices work and approved automatic hold-open devices are operating correctly.",
  },
  {
    id: "fire_hazards",
    label:
      "Are there no obvious fire hazards or excessive combustible materials?",
  },
  {
    id: "exit_mechanisms",
    label:
      "Are all final exits ready for safe evacuation?",
    help:
      "Confirm exit doors are unlocked, emergency release mechanisms work, removable chains and fastenings have been removed, and required gates or shutters are secured open.",
  },
  {
    id: "staffing",
    label:
      "Are sufficient staff present and appropriately fire-inducted?",
  },
  {
    id: "alarm_panel",
    label:
      "Is the fire alarm panel indicating normal operation?",
    help:
      "Record faults and corrective action where applicable.",
  },
  {
    id: "emergency_lighting",
    label:
      "Are there no visible faults with emergency and escape lighting?",
  },
];


function blankAnswer() {
  return {
    answer: "",
    comment: "",
  };
}

const form = reactive({
  fireExits: blankAnswer(),
  cctv: blankAnswer(),
  pest: blankAnswer(),
});

const fire = reactive(
  Object.fromEntries(
    fireQuestions.map((question) => [
      question.id,
      blankAnswer(),
    ])
  )
);

// ---------------------------------------------------------
// MODAL HANDLING
// ---------------------------------------------------------

function refreshFields() {
  nextTick(() => {
    window.M?.updateTextFields?.();
  });
}

function open() {
  resetForm();
  modalInstance?.open();

  nextTick(() => {
    if (contentEl.value) {
      contentEl.value.scrollTop = 0;
    }

    window.M?.updateTextFields?.();
  });
}

function close() {
  modalInstance?.close();
}

function resetForm() {
  venueClosed.value = false;

  for (const question of existingQuestions) {
    form[question.id].answer = "";
    form[question.id].comment = "";
  }

  for (const question of fireQuestions) {
    fire[question.id].answer = "";
    fire[question.id].comment = "";
  }

  error.value = "";

  refreshFields();

  nextTick(() => {
    if (contentEl.value) {
      contentEl.value.scrollTop = 0;
    }
  });
}

// ---------------------------------------------------------
// VALIDATION
// ---------------------------------------------------------

function validate() {
  // Venue closed: no daily checklist required.
  if (venueClosed.value) {
    return "";
  }

  for (const question of existingQuestions) {
    const item = form[question.id];

    if (!item.answer) {
      return `Complete: ${question.label}`;
    }

    if (
      item.answer === question.failure &&
      !item.comment.trim()
    ) {
      return `A comment is required: ${question.label}`;
    }
  }

  for (const question of fireQuestions) {
    const item = fire[question.id];

    if (!item.answer) {
      return `Complete: ${question.label}`;
    }

    if (
      item.answer === "no" &&
      !item.comment.trim()
    ) {
      return `Record the defect: ${question.label}`;
    }
  }

  return "";
}

// ---------------------------------------------------------
// SUBMISSION
// ---------------------------------------------------------

function submitForm() {
  const validation = validate();

  if (validation) {
    error.value = validation;

    window.M?.toast?.({
      html: validation,
    });

    return;
  }

  error.value = "";

  // Only one checkbox is needed for the whole trading day.
  if (venueClosed.value) {
    emit("submit", {
      venue_closed: true,
      checklist_status: "venue_closed",
      checklist_version: 3,
    });

    return;
  }

  const fireChecks = {};

  for (const question of fireQuestions) {
    fireChecks[question.id] = {
      label: question.label,
      answer: fire[question.id].answer,
      comment:
        fire[question.id].answer === "no"
          ? fire[question.id].comment.trim()
          : "",
    };
  }

  // Original Firestore field names are maintained.
  emit("submit", {
    venue_closed: false,

    fire_exits_clear:
      form.fireExits.answer === "yes",

    fire_exits_clear_answer:
      form.fireExits.answer,

    fire_exits_clear_comment:
      form.fireExits.answer === "no"
        ? form.fireExits.comment.trim()
        : "",

    cctv_working_recording:
      form.cctv.answer === "yes",

    cctv_working_recording_answer:
      form.cctv.answer,

    cctv_working_recording_comment:
      form.cctv.answer === "no"
        ? form.cctv.comment.trim()
        : "",

    recent_pest_activity:
      form.pest.answer === "yes",

    recent_pest_activity_answer:
      form.pest.answer,

    recent_pest_activity_comment:
      form.pest.answer === "yes"
        ? form.pest.comment.trim()
        : "",

    fire_safety_checks: fireChecks,

    checklist_version: 3,
  });
}

defineExpose({
  open,
  close,
  resetForm,
});

onMounted(() => {
  if (modalEl.value && window.M?.Modal) {
    modalInstance = window.M.Modal.init(
      modalEl.value,
      {
        dismissible: false,
      }
    );
  }
});

onBeforeUnmount(() => {
  modalInstance?.destroy?.();
});
</script>

<style scoped>
#start-day-check-modal.start-day-modal {
  box-sizing: border-box;
  width: min(760px, 94%) !important;
  height: 82vh !important;
  height: 82dvh !important;
  max-height: calc(100dvh - 24px) !important;
  overflow: hidden !important;
  border-radius: 14px;
  background: #fafafa;
}

#start-day-check-modal.start-day-modal .modal-content {
  box-sizing: border-box;
  position: absolute !important;
  top: 0;
  right: 0;
  bottom: 72px;
  left: 0;
  width: 100%;
  height: calc(100% - 72px) !important;
  max-height: none !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 24px !important;
}

#start-day-check-modal.start-day-modal .modal-footer {
  box-sizing: border-box;
  position: absolute !important;
  right: 0;
  bottom: 0 !important;
  left: 0;
  display: flex !important;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 72px !important;
  min-height: 72px !important;
  margin: 0;
  padding: 12px 20px !important;
  border-top: 1px solid #e0e0e0;
  overflow: hidden;
  background: #fff;
  z-index: 2;
}

.modal-title {
  margin-top: 0;
  color: #263238;
}

.modal-subtitle {
  margin-bottom: 18px;
  color: #455a64;
}

.venue-closed-panel {
  margin: 16px 0 22px;
  padding: 18px;
  border: 2px solid #cfd8dc;
  border-radius: 10px;
  background: #f5f7f8;
  transition: all 0.15s ease;
}

.venue-closed-panel.is-closed {
  border-color: #78909c;
  background: #eceff1;
}

.venue-closed-option {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.venue-closed-title {
  font-size: 17px !important;
  font-weight: 700;
  color: #263238;
}

.venue-closed-description {
  margin: 10px 0 0 35px;
  font-size: 13px;
  line-height: 1.5;
  color: #546e7a;
}

.closed-confirmation {
  margin: 15px 0 0;
  padding: 12px;
  background: #ffffff;
  border-left: 4px solid #607d8b;
  border-radius: 4px;
  color: #37474f;
  font-size: 13px;
  line-height: 1.6;
}

.section-heading {
  margin: 22px 0 12px;
  padding-bottom: 9px;
  border-bottom: 2px solid #00897b;
  color: #263238;
  font-size: 17px;
  font-weight: 700;
}

.question-card {
  margin: 10px 0;
  padding: 15px;
  border-radius: 10px;
}

.question-title {
  margin: 0 0 10px;
  color: #263238;
  font-weight: 600;
}

.question-help {
  margin: -4px 0 12px;
  color: #607d8b;
  font-size: 13px;
}

.answer-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.comment-box {
  margin-top: 15px;
}

.comment-box textarea {
  box-sizing: border-box;
  width: 100%;
  min-height: 85px;
  padding: 10px;
  border: 1px solid #cfd8dc;
  border-radius: 7px;
  resize: vertical;
}

.safety-notice {
  padding: 12px;
  border-left: 4px solid #ef6c00;
  background: #fff8e1;
  color: #5d4037;
  font-size: 13px;
}

.error-message {
  margin-top: 15px;
}

@media (max-width: 600px) {
  #start-day-check-modal.start-day-modal {
    width: 94% !important;
    height: 82dvh !important;
    max-height: calc(100dvh - 16px) !important;
  }

  #start-day-check-modal.start-day-modal .modal-content {
    bottom: 92px;
    height: calc(100% - 92px) !important;
    padding: 16px !important;
  }

  #start-day-check-modal.start-day-modal .modal-footer {
    height: 92px !important;
    min-height: 92px !important;
    flex-direction: column-reverse;
    align-items: stretch;
    padding: 9px 16px !important;
  }

  .modal-footer button {
    width: 100%;
  }

  .question-card {
    padding: 12px;
  }

  .answer-row {
    gap: 16px;
  }

  .modal-title {
    font-size: 22px;
  }

  .venue-closed-panel {
    padding: 14px;
  }

  .venue-closed-description {
    margin-left: 0;
  }
}
</style>
